import { useCallback, useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { ArrowDownToLine, Bell, Code2, LayoutGrid, Link2, Lock, LogOut, Menu, Send, Settings, Smartphone, X, Zap } from "lucide-react";
import Logo from "../components/Logo";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { routingPlan } from "../lib/vpa";

const INK = "#14101F";
const VIOLET = "#4931E8";
const MINT = "#1FBF75";

const NAV = [
  { key: "overview", group: "Collect", path: "/overview", icon: LayoutGrid, label: "Overview", title: "Overview", sub: "Your collection, at a glance." },
  { key: "links", group: "Collect", path: "/links", icon: Link2, label: "Payment Links", title: "Payment Links", sub: "Spin up a QR or share-link in seconds." },
  { key: "deposits", group: "Collect", path: "/deposits", icon: ArrowDownToLine, label: "Deposits", title: "Deposits", sub: "Every rupee, every payer, every RRN." },
  { key: "devices", group: "Collect", path: "/devices", icon: Smartphone, label: "Devices", title: "Devices", sub: "Your connected phones + notification-capture health." },
  { key: "payin-gateway", group: "Gateway", path: "/payin-gateway", icon: Zap, gate: "payin", label: "Auto Pay", title: "Auto Pay", sub: "Collect payments automatically — every transaction, live, with its callbacks." },
  { key: "payout", group: "Gateway", path: "/payout", icon: Send, gate: "payout", label: "Payout", title: "Payout", sub: "Bank payouts from your SuperCash balance." },
  { key: "account", group: "Settings", path: "/account", icon: Settings, label: "My Account", title: "My Account", sub: "Business profile, bank, and merchant UPIs." },
  { key: "developers", group: "Settings", path: "/developers", icon: Code2, label: "Developers", title: "Developers", sub: "API keys + integration guides." },
  { key: "callbacks", group: "Settings", path: "/callbacks", icon: Bell, label: "Callbacks", title: "Callbacks", sub: "Webhook delivery — signed, retried, logged." },
];
const NAV_GROUPS = ["Collect", "Gateway", "Settings"];

const RED = "#b3261e";
const AMBER = "#b26a00";
const MODES = [
  { key: "p2m", label: "P2M · Merchant", fails: "UPI payments" },
  { key: "p2p", label: "P2P · Personal", fails: "P2P orders" },
];

// Sidebar "Settles to" card. Renders the backend's routing plan per mode, so
// what it shows is exactly where the next payment of each mode will land.
// Compact: one line per mode (badge · UPI ID · status dot); detail only when something needs attention.
function SettlesCard({ state, plan, onOpen, onRetry }) {
  if (state !== "ready") {
    return (
      <div className="sc-settle" data-testid="sidebar-settles-card" data-state={state}>
        <div className="sc-settle-head"><span className="sc-settle-label">Settles to</span></div>
        {state === "error" ? (
          <div className="sc-settle-note">Couldn't load your UPI IDs. <button type="button" onClick={onRetry} className="sc-settle-retry">Retry</button></div>
        ) : (
          <div className="sc-settle-note">Loading…</div>
        )}
      </div>
    );
  }

  // A mode "works" when its payments land somewhere — its own pool, or the other
  // mode's pool via fallback. Red only when no payment of any mode can land.
  const working = MODES.filter((m) => plan[m.key].routes_to);
  const nothing = MODES.every((m) => plan[m.key].status === "none");
  const labelOf = (key) => MODES.find((m) => m.key === key)?.label.split(" · ")[0];

  return (
    <button type="button" onClick={onOpen} title="Manage UPI IDs & routing" className={`sc-settle is-btn ${!working.length ? "is-bad" : ""}`} data-testid="sidebar-settles-card" data-state="ready">
      <div className="sc-settle-head">
        <span className="sc-settle-label" style={{ color: working.length ? VIOLET : RED }}>{working.length ? "Settles to" : "No active UPI"}</span>
        {working.length > 0 && (
          <span className="sc-settle-pill" data-testid="sidebar-routing-mode">{plan.autoshuffle ? "Shuffle" : "Pinned"}</span>
        )}
      </div>

      {nothing ? (
        <div className="sc-settle-note" style={{ color: RED }}>
          {MODES.some((m) => (plan[m.key].placeholders || []).length)
            ? "Your UPI ID is a placeholder — add your real UPI ID in My Account."
            : "Add a UPI ID in My Account to accept payments."}
        </div>
      ) : (
        MODES.map((m) => {
          const p = plan[m.key];
          const via = p.routes_to && p.routes_to !== m.key ? labelOf(p.routes_to) : null;
          const off = p.status === "disabled";
          const none = p.status === "none";
          const tone = none ? "#a19aae" : off ? (via ? AMBER : RED) : p.status === "all_capped" ? AMBER : MINT;
          let note = "";
          if (none) note = via ? `via ${via}` : "not set up";
          else if (off) note = via ? `all off · via ${via}` : `all off — ${m.fails} will fail`;
          else if (p.status === "all_capped") note = "all at daily cap";
          else if (p.capped.length) note = `${p.capped.length} maxed today`;
          else if (p.live.length > 1) note = `spreading across ${p.live.length}`;
          return (
            <div key={m.key} className="sc-settle-row" data-testid={`sidebar-settle-${m.key}`} data-status={p.status}>
              <span className="sc-settle-mode">{labelOf(m.key)}</span>
              <span className="sc-settle-vpa" data-testid={`sidebar-settle-vpa-${m.key}`} title={p.next || ""}>
                {!off && !none ? p.next : <span style={{ color: tone, fontFamily: "Inter, sans-serif", fontSize: 11 }}>{note}</span>}
              </span>
              <span className="sc-settle-dot" style={{ background: tone }} title={note || "Active"} />
              {!off && !none && note && <span className="sc-settle-sub">{note}</span>}
            </div>
          );
        })
      )}
    </button>
  );
}

function initialsOf(name = "") {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "SC"
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [merchant, setMerchant] = useState(null);
  const [upiIds, setUpiIds] = useState([]);
  const [serverRouting, setServerRouting] = useState(null);
  // "loading" until the first /merchants/me answers; a failed load must not be
  // mistaken for "no UPI configured" (that used to paint the card red).
  const [loadState, setLoadState] = useState("loading");
  const [sideOpen, setSideOpen] = useState(false);
  // Auto Pay / Payout exist only once God mode activates them for this merchant.
  const [gw, setGw] = useState({ loaded: false, payin: false, payout: false });

  useEffect(() => {
    let alive = true;
    api.get("/gateway/profile")
      .then(({ data }) => alive && setGw({ loaded: true, payin: !!data.payin_enabled, payout: !!data.payout_enabled }))
      .catch(() => alive && setGw((g) => ({ ...g, loaded: true })));
    return () => { alive = false; };
  }, []);

  const loadMerchant = useCallback(async () => {
    try {
      const { data } = await api.get("/merchants/me");
      setMerchant(data.merchant);
      setUpiIds(data.upi_ids || []);
      setServerRouting(data.routing || null);
      setLoadState("ready");
    } catch {
      // keep showing the last good data if we have it
      setLoadState((s) => (s === "ready" ? s : "error"));
    }
  }, []);

  useEffect(() => {
    loadMerchant();
    // Daily caps + today counts drift as payments settle: refresh on focus and
    // once a minute while the tab is visible.
    const onFocus = () => loadMerchant();
    const t = setInterval(() => {
      if (document.visibilityState === "visible") loadMerchant();
    }, 60_000);
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", onFocus);
    };
  }, [loadMerchant]);

  useEffect(() => {
    setSideOpen(false);
  }, [location.pathname]);

  // Lock the *background page* scroll while the mobile sidebar drawer is
  // open, without breaking touch-scrolling inside the drawer itself.
  // Uses the "fixed body" trick (preserves visible scroll position, works on
  // every mobile browser including iOS Safari) instead of `overflow:hidden`
  // which can swallow touch events on a fixed child on some webkit builds.
  useEffect(() => {
    if (!sideOpen) return;
    const scrollY = window.scrollY;
    const body = document.body;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
    };
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    return () => {
      Object.assign(body.style, prev);
      window.scrollTo(0, scrollY);
    };
  }, [sideOpen]);

  const activeKey = (() => {
    const match = NAV.find((n) => location.pathname === n.path || location.pathname.startsWith(n.path + "/"));
    return match?.key || "overview";
  })();
  const activeMeta = NAV.find((n) => n.key === activeKey);
  const nav = NAV.filter((n) => !n.gate || gw[n.gate]);
  const locked = activeMeta?.gate && !gw[activeMeta.gate];

  const routing = routingPlan(upiIds, merchant, serverRouting);

  return (
    <div data-testid="dashboard-shell" style={{ fontFamily: "Inter, sans-serif", color: INK, background: "#F5F2EC", minHeight: "100vh", display: "flex" }}>
      {sideOpen && <div onClick={() => setSideOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(20,16,31,.45)", zIndex: 70 }} />}

      {/* SIDEBAR */}
      <aside
        data-testid="dashboard-sidebar"
        className="sc-side sc-scroll"
        style={{
          width: 224,
          flex: "none",
          background: "#fff",
          borderRight: `2.5px solid ${INK}`,
          minHeight: "100vh",
          position: "sticky",
          top: 0,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          padding: "12px 10px 12px",
          zIndex: 75,
          overflowY: "auto",
          overflowX: "hidden",
          overscrollBehavior: "contain",
          WebkitOverflowScrolling: "touch",
          touchAction: "pan-y",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 14, flex: "none" }}>
          {/* The SVG has built-in whitespace: render it wide and let the box clip the empty top/bottom. */}
          <Link to="/" style={{ height: 74, display: "flex", alignItems: "center", textDecoration: "none", overflow: "hidden", marginLeft: -6 }}>
            <Logo height={90} />
          </Link>
          <button className="sc-side-close" onClick={() => setSideOpen(false)} aria-label="Close menu"><X size={16} strokeWidth={2.4} /></button>
        </div>

        <nav className="sc-side-nav">
          {NAV_GROUPS.filter((g) => nav.some((n) => n.group === g)).map((g) => (
            <div key={g} className="sc-side-group">
              <div className="sc-side-group-label">{g}</div>
              {nav.filter((n) => n.group === g).map((n) => {
                const on = activeKey === n.key;
                const Icon = n.icon;
                return (
                  <button
                    key={n.key}
                    data-testid={`sidebar-nav-${n.key}`}
                    onClick={() => navigate(n.path)}
                    aria-current={on ? "page" : undefined}
                    className={`sc-nav-link sc-side-item ${on ? "active" : ""}`}
                  >
                    <Icon className="sc-side-icon" size={18} strokeWidth={on ? 2.4 : 2} aria-hidden="true" />
                    <span className="sc-side-text">{n.label}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        <div style={{ marginTop: "auto", paddingTop: 14, display: "flex", flexDirection: "column", gap: 8, flex: "none" }}>
          <SettlesCard state={loadState} plan={routing} onOpen={() => navigate("/account")} onRetry={loadMerchant} />
          <div className="sc-side-profile">
            <div className="sc-side-avatar">{initialsOf(merchant?.name || user?.name || user?.email)}</div>
            <div style={{ lineHeight: 1.25, minWidth: 0, flex: 1 }}>
              <div className="sc-side-name">{merchant?.name || user?.email}</div>
              {user?.email && merchant?.name && <div className="sc-side-email">{user.email}</div>}
            </div>
            <button data-testid="sidebar-logout" className="sc-side-logout" title="Log out" aria-label="Log out" onClick={async () => { await logout(); navigate("/"); }}><LogOut size={15} strokeWidth={2.2} /></button>
          </div>
        </div>
      </aside>

      {/* MAIN */}
      <main style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <header className="sc-dash-topbar" style={{ position: "sticky", top: 0, zIndex: 30, background: "rgba(245,242,236,0.88)", backdropFilter: "blur(8px)", borderBottom: `2.5px solid ${INK}`, padding: "14px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 11, minWidth: 0, flex: 1 }}>
            <button data-testid="dashboard-burger" onClick={() => setSideOpen((v) => !v)} className="sc-burger" style={{ display: "none", cursor: "pointer", flex: "none", width: 38, height: 38, alignItems: "center", justifyContent: "center", border: `2.5px solid ${INK}`, borderRadius: 11, background: "#fff", boxShadow: `3px 3px 0 ${INK}`, fontSize: 16 }}><Menu size={18} strokeWidth={2.4} /></button>
            <div style={{ minWidth: 0 }}>
              <h1 className="sc-dash-title" style={{ fontFamily: "Inter, sans-serif", fontWeight: 800, fontSize: 23, letterSpacing: "-0.02em", margin: 0, textTransform: "uppercase", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} data-testid="dashboard-page-title">{activeMeta?.title}</h1>
              <p className="sc-dash-sub" style={{ margin: "1px 0 0", fontSize: 12.5, color: "#6a6478" }}>{activeMeta?.sub}</p>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flex: "none" }}>
            <span className="sc-live-chip" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "JetBrains Mono, monospace", fontSize: 11.5, fontWeight: 700, background: "#fff", border: `2px solid ${INK}`, borderRadius: 100, padding: "6px 11px" }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: MINT }} /> LIVE
            </span>
            <button data-testid="topbar-new-link" onClick={() => navigate("/links")} className="sc-btn sc-btn-violet sc-new-link-btn" style={{ padding: "9px 18px", fontSize: 13.5, boxShadow: `4px 4px 0 ${INK}`, whiteSpace: "nowrap" }}><span style={{ fontSize: 16, lineHeight: 1 }}>+</span> New link</button>
          </div>
        </header>

        <div className="sc-dash-body" style={{ padding: "22px 28px 56px", flex: 1, minWidth: 0, width: "100%", maxWidth: 1480, margin: "0 auto" }}>
          {locked ? (
            gw.loaded ? (
              <div data-testid="feature-locked" style={{ maxWidth: 460, margin: "48px auto", textAlign: "center", background: "#fff", border: `2.5px solid ${INK}`, borderRadius: 16, boxShadow: `4px 4px 0 ${INK}`, padding: "28px 24px" }}>
                <div style={{ width: 48, height: 48, margin: "0 auto 12px", borderRadius: 14, background: "#ECE8FB", border: `2px solid ${INK}`, display: "flex", alignItems: "center", justifyContent: "center" }}><Lock size={22} color={VIOLET} strokeWidth={2.4} /></div>
                <h2 style={{ fontFamily: "Inter, sans-serif", fontWeight: 800, fontSize: 20, margin: "0 0 6px" }}>{activeMeta.title} isn't activated</h2>
                <p style={{ margin: 0, fontSize: 13.5, color: "#51495f" }}>This feature is switched on by SuperCash for each account. Contact SuperCash support to activate it.</p>
              </div>
            ) : null
          ) : (
            <Outlet context={{ merchant, upiIds, routing, reload: loadMerchant }} />
          )}
        </div>
      </main>

      <style>{`
        .sc-side-close { display: none; width: 36px; height: 36px; flex: none; border-radius: 11px; border: 2.5px solid ${INK}; background: #fff; box-shadow: 3px 3px 0 ${INK}; cursor: pointer; font-size: 14px; color: ${INK}; }
        .sc-side-nav { display: flex; flex-direction: column; gap: 10px; flex: none; }
        .sc-side-group { display: flex; flex-direction: column; gap: 2px; }
        .sc-side-group-label { font-family: "JetBrains Mono", monospace; font-size: 9px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; color: #a19aae; padding: 0 10px 3px; }
        .sc-side-item { position: relative; width: 100%; height: 38px; text-align: left; display: flex !important; align-items: center; gap: 10px !important; padding: 0 10px !important; font-size: 13.5px !important; font-weight: 500; border: 2px solid transparent !important; border-radius: 10px !important; background: transparent; color: #3b3449; cursor: pointer; transition: background .12s, color .12s; }
        .sc-side-item:hover { background: #F4F1FD; color: ${INK}; }
        .sc-side-item:focus-visible { outline: 2.5px solid ${VIOLET}; outline-offset: 1px; }
        .sc-side-item.active { background: ${VIOLET} !important; color: #fff !important; border-color: ${INK} !important; box-shadow: 2px 2px 0 ${INK}; font-weight: 700; }
        .sc-side-icon { flex: none; color: #6a6478; transition: color .12s; }
        .sc-side-item:hover .sc-side-icon { color: ${VIOLET}; }
        .sc-side-item.active .sc-side-icon { color: #fff; }
        .sc-side-text { flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .sc-settle { width: 100%; text-align: left; font: inherit; color: ${INK}; background: linear-gradient(135deg, #F5F2FF 0%, #EEF4FF 55%, #F2FBF6 100%); border: 1px solid #DED7F5; border-radius: 12px; padding: 9px 11px; display: flex; flex-direction: column; gap: 6px; }
        .sc-settle.is-btn { cursor: pointer; transition: border-color .12s, box-shadow .12s; }
        .sc-settle.is-btn:hover { border-color: ${VIOLET}; box-shadow: 0 4px 14px -8px rgba(73,49,232,.45); }
        .sc-settle.is-bad { background: linear-gradient(135deg, #FFF1EF 0%, #FFF7F0 100%); border-color: #F2C2BB; }
        .sc-settle-head { display: flex; align-items: center; justify-content: space-between; gap: 6px; }
        .sc-settle-label { font-size: 10px; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; }
        .sc-settle-pill { font-size: 9.5px; font-weight: 600; color: #575166; background: rgba(255,255,255,.75); border: 1px solid #DED7F5; border-radius: 100px; padding: 1px 7px; }
        .sc-settle-row { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; column-gap: 7px; row-gap: 1px; }
        .sc-settle-mode { font-size: 9.5px; font-weight: 700; color: ${VIOLET}; background: #fff; border: 1px solid #DED7F5; border-radius: 6px; padding: 1px 5px; line-height: 1.4; }
        .sc-settle-vpa { font-family: "JetBrains Mono", monospace; font-size: 11.5px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .sc-settle-dot { width: 7px; height: 7px; border-radius: 50%; flex: none; }
        .sc-settle-sub { grid-column: 2 / 4; font-size: 10px; color: #575166; }
        .sc-settle-note { font-size: 11px; color: #575166; line-height: 1.4; }
        .sc-settle-retry { background: none; border: 0; padding: 0; font: inherit; font-weight: 700; color: ${VIOLET}; cursor: pointer; }
        .sc-side-profile { display: flex; align-items: center; gap: 9px; padding: 8px; border: 2px solid ${INK}; border-radius: 12px; background: #fff; box-shadow: 2px 2px 0 ${INK}; }
        .sc-side-avatar { width: 32px; height: 32px; border-radius: 9px; background: ${VIOLET}; border: 2px solid ${INK}; color: #fff; display: flex; align-items: center; justify-content: center; font-family: "Inter", sans-serif; font-weight: 800; font-size: 14px; flex: none; }
        .sc-side-name { font-weight: 700; font-size: 13px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .sc-side-email { font-family: "JetBrains Mono", monospace; font-size: 10px; color: #7a7488; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .sc-side-logout { flex: none; width: 30px; height: 30px; padding: 0; border-radius: 9px; border: 2px solid ${INK}; background: #fff; font-size: 15px; line-height: 1; color: ${INK}; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }
        .sc-side-logout:hover { background: #FBE9E7; border-color: #9c1f16; color: #9c1f16; }
        @media (max-width: 980px) {
          .sc-side-close { display: inline-flex; align-items: center; justify-content: center; }
          .sc-side { width: min(300px, 86vw) !important; height: 100dvh !important; position: fixed !important; left: 0 !important; top: 0 !important; height: 100vh !important; transform: translateX(${sideOpen ? "0" : "-105%"}) !important; transition: transform .2s ease; box-shadow: ${sideOpen ? "8px 0 0 rgba(20,16,31,0.1)" : "none"}; }
          .sc-burger { display: inline-flex !important; }
          .sc-dash-topbar { padding: 11px 14px !important; gap: 10px !important; }
          .sc-dash-title { font-size: 18px !important; }
          .sc-dash-sub { display: none !important; }
          .sc-live-chip { display: none !important; }
          .sc-new-link-btn { font-size: 12.5px !important; padding: 8px 13px !important; box-shadow: 3px 3px 0 #14101F !important; }
          .sc-cb-grid, .sc-cards-grid, .sc-board { grid-template-columns: minmax(0, 1fr) !important; }
          .sc-dash-body { padding: 16px 14px 48px !important; }
        }
        @media (max-width: 480px) {
          .sc-dash-body { padding: 12px 12px 44px !important; }
          .sc-dash-title { font-size: 16.5px !important; }
          .sc-new-link-btn { font-size: 12px !important; padding: 7px 11px !important; gap: 5px !important; }
          .sc-burger { width: 34px !important; height: 34px !important; font-size: 15px !important; box-shadow: 2px 2px 0 #14101F !important; }
        }
      `}</style>
    </div>
  );
}
